import React from 'react';
import { theme, Tooltip } from 'antd';
import { IconBriefcase, IconEngine, IconTool, IconUsers } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { CORAL } from '../theme/creamyTokens';

export type MapExploreCategory = 'machinery' | 'accessories' | 'operators' | 'services';

interface MapCategoryOption {
  key: MapExploreCategory;
  icon: React.ReactNode;
  labelKey: string;
}

// Same icon set already established for these categories app-wide -- see
// AppHeader's "+" menu and HOST_TOOLS_NAV, and the briefcase ServiceForm's
// own first wizard phase already uses for a service.
const OPTIONS: MapCategoryOption[] = [
  { key: 'machinery', icon: <IconEngine size={20} />, labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_SECTION_MACHINERY },
  { key: 'accessories', icon: <IconTool size={20} />, labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORIES_NAV_LABEL },
  { key: 'operators', icon: <IconUsers size={20} />, labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPERATORS_NAV_LABEL },
  { key: 'services', icon: <IconBriefcase size={20} />, labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICES_NAV_LABEL },
];

interface MapCategoryPickerProps {
  active: MapExploreCategory;
  onChange: (category: MapExploreCategory) => void;
}

/**
 * Semi-transparent pill bar sitting just under the map's search bar, over the
 * map -- lets a guest switch which resource type the map/list/search bar/
 * filters are currently showing, without leaving the map. Starts on
 * 'machinery' (this screen's own long-standing default).
 *
 * Services took a while to arrive here, and the reason is worth keeping: this
 * comment used to say a Service had no map-plottable location of its own. It
 * does -- its own City/Latitude/Longitude, picked on the create form like a
 * machine's -- and unlike Machinery/Accessories/Operators it does not borrow a
 * machine's coordinates, it has its own address. What was missing was the
 * viewport filter on the API side, not the data.
 */
const MapCategoryPicker: React.FC<MapCategoryPickerProps> = ({ active, onChange }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: 4,
        borderRadius: 999,
        background: 'rgba(255,255,255,0.82)',
        backdropFilter: 'blur(6px)',
        boxShadow: '0 4px 14px rgba(0,0,0,0.16)',
      }}
    >
      {OPTIONS.map((option) => {
        const selected = option.key === active;
        return (
          <Tooltip key={option.key} title={translate(t, option.labelKey)}>
            <button
              type="button"
              aria-label={translate(t, option.labelKey)}
              aria-pressed={selected}
              onClick={() => onChange(option.key)}
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                border: 'none',
                background: selected ? CORAL : 'transparent',
                color: selected ? '#fff' : token.colorTextSecondary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'background 150ms ease, color 150ms ease',
              }}
            >
              {option.icon}
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
};

export default MapCategoryPicker;
